import type { ReactNode } from 'react';
import type { HostedCheckoutReturnState } from '../types';

export type PaymentReturnNoticeProps = {
  state: HostedCheckoutReturnState;
  noticeKey?: string;
  successTitle: string;
  successBody: string;
  cancelTitle: string;
  cancelBody: string;
  guidanceTitle?: string;
  guidanceBody?: string;
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
};

const toneClasses = (state: HostedCheckoutReturnState): string => {
  if (state === 'success') {
    return 'border-[#28E2A7]/30 bg-[#ECFDF5] text-[#065F46]';
  }
  if (state === 'cancel') {
    return 'border-[#F59E0B]/30 bg-[#FFFBEB] text-[#92400E]';
  }
  return '';
};

export function PaymentReturnNotice({
  state,
  noticeKey,
  successTitle,
  successBody,
  cancelTitle,
  cancelBody,
  guidanceTitle = 'What happens next',
  guidanceBody,
  primaryAction,
  secondaryAction,
}: PaymentReturnNoticeProps) {
  if (!state) {
    return null;
  }

  const title = state === 'success' ? successTitle : cancelTitle;
  const body = state === 'success' ? successBody : cancelBody;

  return (
    <div
      data-testid="payment-return-notice"
      data-payment-return-state={state}
      {...(noticeKey ? { 'data-payment-return-key': noticeKey } : {})}
      className={`rounded-xl border px-5 py-4 shadow-sm ${toneClasses(state)}`}
    >
      <p className="text-sm font-semibold">{title}</p>
      <p className="mt-1 text-sm opacity-90">{body}</p>
      {guidanceBody ? (
        <div className="mt-3 rounded-lg bg-white/60 px-3 py-2 text-xs text-current/90">
          <p className="font-semibold">{guidanceTitle}</p>
          <p className="mt-1">{guidanceBody}</p>
        </div>
      ) : null}
      {primaryAction || secondaryAction ? (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="payment-return-notice-actions">
          {primaryAction}
          {secondaryAction}
        </div>
      ) : null}
    </div>
  );
}
